// Builds the voxel USS New Jersey (BB-62) at 1:1 scale — hull, armor,
// superstructure, and a representative instance of every kind of space a
// sailor would know: bridge, CIC, main-battery plot, magazine, engine and
// fire rooms, mess deck, berthing, sick bay, wardroom, radio, DC central,
// brig, even the gedunk stand. Also emits the gameplay metadata the rest of
// the sim consumes: named compartments, interactive stations, crew hangout
// points, turret pivots and ladder trunks.
//
// Ship-local frame: +X stern->bow, +Y keel->masthead, +Z port->starboard.

import * as THREE from "three";
import { V, GRID_X, GRID_Y, GRID_Z, CENTER_Z, WATERLINE_Y } from "./const.js";
import { VoxelGrid, gridToMeshes } from "./voxel.js";

const CZ = CENTER_Z; // 17

// Hull half-beam at grid x (stern tip x=1, bow tip x=271).
export function halfBeam(x) {
  const xm = x - 1;
  if (xm < 0 || xm > 270) return 0;
  if (xm >= 80 && xm <= 195) return 16.5;
  if (xm > 195) { // long fine bow
    const t = (xm - 195) / 75;
    return Math.max(0.6, 16.5 * Math.pow(1 - Math.pow(t, 1.7), 1.15));
  }
  const u = 1 - xm / 80; // cruiser stern
  return 16.5 * (1 - Math.pow(u, 2.4) * 0.72);
}
function keelY(x) {
  const xm = x - 1;
  if (xm > 240) return 9 * Math.pow((xm - 240) / 30, 1.5);
  if (xm < 40) return 6 * Math.pow((40 - xm) / 40, 1.7);
  return 0;
}

export function buildShip() {
  const g = new VoxelGrid(GRID_X, GRID_Y, GRID_Z);
  const rooms = [];    // {key, name, deck, x0,x1, y0,y1, z0,z1}
  const stations = []; // filled by station() helper
  const hangouts = []; // [x,y,z] spots idle sailors drift between
  const ladders = [];  // ladder columns for the deck map

  const room = (key, name, deckWalk, x0, x1, z0, z1) => {
    rooms.push({ key, name, x0, x1, z0, z1, y0: deckWalk, y1: deckWalk + 2, walk: deckWalk });
  };
  const station = (id, name, x, y, z, yaw = 0, extra = {}) => {
    stations.push({ id, name, pos: new THREE.Vector3(x, y, z), yaw, ...extra });
  };
  const hangout = (x, y, z) => hangouts.push(new THREE.Vector3(x + 0.5, y, z + 0.5));

  // ---- hull: solid up through the main-deck slab (y13), then carved ------
  for (let x = 1; x <= 271; x++) {
    const hb = halfBeam(x);
    const ky = Math.floor(keelY(x));
    for (let y = ky; y <= 13; y++) {
      // bilge tuck-in near the keel
      const hbY = y < 5 ? hb * (0.5 + 0.5 * (y / 5)) : hb;
      const z0 = Math.ceil(CZ - hbY + 0.5), z1 = Math.floor(CZ + hbY - 0.5);
      if (z1 < z0) continue;
      const t = y <= 8 ? V.BOTTOM : y === 13 ? V.DECKWOOD : V.HULL;
      g.fill(x, x, y, y, z0, z1, t);
    }
    // keep a hull-gray ring on the deck edge so the wood reads as planking
    const z0 = Math.ceil(CZ - hb + 0.5), z1 = Math.floor(CZ + hb - 0.5);
    if (z1 >= z0) { g.set(x, 13, z0, V.HULL); g.set(x, 13, z1, V.HULL); }
  }
  // Bow bulwark and stern rail block
  for (let x = 236; x <= 271; x++) {
    const hb = halfBeam(x);
    const z0 = Math.ceil(CZ - hb + 0.5), z1 = Math.floor(CZ + hb - 0.5);
    if (z1 >= z0) { g.set(x, 14, z0, V.HULL); g.set(x, 14, z1, V.HULL); }
  }

  // ---- below-decks interior: carve big slabs of air between deck plates --
  // Second deck (walk 11) and third deck (walk 8) run most of the length.
  const carveDeck = (walk, x0, x1, inset) => {
    for (let x = x0; x <= x1; x++) {
      const hb = halfBeam(x) - inset;
      if (hb < 2) continue;
      const z0 = Math.ceil(CZ - hb + 0.5), z1 = Math.floor(CZ + hb - 0.5);
      g.fill(x, x, walk, walk + 1, z0, z1, V.AIR);
      // paint the floor below the carve
      for (let z = z0; z <= z1; z++) if (g.solid(x, walk - 1, z)) g.set(x, walk - 1, z, V.FLOOR);
    }
  };
  carveDeck(11, 26, 240, 1.5);  // second deck
  carveDeck(8, 46, 214, 2.5);   // third deck

  // Machinery spaces: halls from the tank top up to the third-deck plating.
  const machSpace = (x0, x1) => {
    g.carve(x0, x1, 3, 6, 6, 28);
    for (let x = x0; x <= x1; x++) for (let z = 6; z <= 28; z++) g.set(x, 2, z, V.DECKSTEEL);
  };
  machSpace(92, 116);  // engine room
  machSpace(120, 144); // fire room (boilers)
  g.fill(117, 119, 3, 6, 6, 28, V.HULL); // split bulkhead
  g.fill(117, 119, 3, 4, 16, 18, V.AIR); // WT door between them (full thickness)

  // Boilers: six drums in the fire room, uptakes rising through the plating
  for (const bx of [123, 130, 137]) {
    g.fill(bx, bx + 4, 3, 6, 8, 13, V.MACH);
    g.fill(bx, bx + 4, 3, 6, 21, 26, V.MACH);
    g.fill(bx + 2, bx + 2, 7, 9, 10, 11, V.PIPE); // uptakes
    g.fill(bx + 2, bx + 2, 7, 9, 23, 24, V.PIPE);
  }
  // Turbines + reduction gears in the engine room
  g.fill(96, 108, 3, 5, 9, 13, V.MACH);
  g.fill(96, 108, 3, 5, 21, 25, V.MACH);
  // (machinery reaches y5; the y6 clear row keeps headroom under the plating)
  g.fill(98, 106, 3, 4, 16, 18, V.PIPE); // steam mains on the centerline
  g.fill(110, 112, 3, 5, 10, 12, V.MACH);
  g.fill(110, 112, 3, 5, 22, 24, V.MACH);
  g.fill(97, 97, 3, 4, 19, 20, V.CONSOLE); // throttle board
  g.fill(96, 96, 4, 4, 19, 20, V.GAUGE);   // steam pressure gauges above the wheels
  g.fill(93, 93, 3, 5, 12, 16, V.GAUGE);   // main gauge board on the fwd bulkhead
  g.fill(94, 114, 6, 6, 8, 8, V.PIPE);     // overhead steam mains, port and stbd
  g.fill(94, 114, 6, 6, 26, 26, V.PIPE);
  g.fill(109, 109, 3, 4, 16, 18, V.BRASS); // throttle wheels on the centerline mains
  room("engine", "ENGINE ROOM NO. 2", 3, 92, 116, 6, 28);
  g.fill(121, 143, 6, 6, 17, 17, V.PIPE);  // fire room: auxiliary steam header overhead
  g.fill(143, 143, 3, 5, 15, 19, V.GAUGE); // boiler water-level gauge board
  for (const bx of [123, 130, 137]) {      // firebox doors glowing on each boiler front
    g.set(bx + 2, 3, 14, V.RED);
    g.set(bx + 2, 3, 20, V.RED);
  }
  room("fireroom", "FIRE ROOM NO. 4", 3, 120, 144, 6, 28);
  station("throttle", "MAIN ENGINE THROTTLE", 99.5, 3, 19.5, Math.PI, { kind: "throttle" });
  hangout(103, 3, 19); hangout(126, 3, 17);

  // ---- second deck (walk 11): the living ship ---------------------------
  const wall2 = (x0, x1, z0, z1) => boxWalls(g, x0, x1, 11, 12, z0, z1, V.WALL);
  // fore-aft passageway, centerline
  // (already open; put wall runs to shape rooms off it)
  // Crew mess
  wall2(100, 142, 5, 28); door(g, 121, 11, 28); door(g, 121, 11, 5); door(g, 100, 11, 17); door(g, 142, 11, 17);
  room("mess", "CREW'S MESS", 11, 100, 142, 5, 28);
  for (let mx = 106; mx <= 136; mx += 8)
    for (const mz of [9, 14, 20, 25]) {
      g.fill(mx, mx + 3, 11, 11, mz, mz, V.TABLE);
      hangout(mx + 1, 11, mz - 1);
    }
  g.fill(101, 102, 11, 11, 8, 14, V.GALLEY);  // steam-line serving counter
  g.fill(101, 101, 11, 12, 20, 24, V.GALLEY); // scullery / tray return
  g.fill(103, 103, 11, 11, 8, 8, V.WHITE);    // milk machine — the fleet's pride
  g.fill(141, 141, 12, 12, 8, 12, V.CABLE);   // wall conduit
  g.fill(141, 141, 12, 12, 22, 26, V.CABLE);
  // Berthing
  wall2(146, 170, 5, 15); door(g, 146, 11, 10); door(g, 158, 11, 15);
  g.fill(170, 170, 11, 12, 8, 9, V.AIR); // after door to the frame 174 passage
  room("berthing", "CREW BERTHING 2-146-0-L", 11, 146, 170, 5, 15);
  for (let bx = 149; bx <= 167; bx += 4) {
    g.fill(bx, bx + 1, 11, 11, 6, 6, V.BUNK); g.fill(bx, bx + 1, 12, 12, 7, 7, V.BUNK);
    g.fill(bx, bx + 1, 11, 11, 13, 13, V.BUNK); g.fill(bx, bx + 1, 12, 12, 12, 12, V.BUNK);
    g.set(bx + 2, 11, 6, V.LOCKER);            // seabag lockers between the racks
    g.set(bx + 2, 11, 13, V.LOCKER);
    hangout(bx, 11, 9);
  }
  // Damage Control Central
  wall2(146, 162, 18, 27); door(g, 146, 11, 22); door(g, 154, 11, 18);
  room("dccentral", "DAMAGE CONTROL CENTRAL", 11, 146, 162, 18, 27);
  g.fill(152, 158, 11, 12, 26, 26, V.CONSOLE); // status boards
  g.fill(153, 157, 12, 12, 26, 26, V.GAUGE);   // lit stability / flooding gauges
  g.fill(148, 149, 11, 11, 20, 21, V.TABLE);
  g.fill(147, 147, 11, 12, 19, 19, V.RED); g.fill(161, 161, 11, 12, 26, 26, V.RED);
  g.fill(147, 147, 11, 11, 24, 25, V.LOCKER);  // OBA and pattern lockers
  g.fill(150, 150, 12, 12, 18, 18, V.WHITE);   // ship's plans rack on the fwd wall
  station("dcboard", "DC STATUS BOARD", 155.5, 11, 24.5, Math.PI / 2, { kind: "dc" });
  hangout(150, 11, 23); hangout(158, 11, 21);
  // Officers' staterooms forward (doors clear of turret I's barbette)
  wall2(190, 214, 6, 28); door(g, 190, 11, 9); door(g, 190, 11, 25);
  g.fill(202, 202, 11, 12, 7, 27, V.WALL); door(g, 202, 11, 12); door(g, 202, 11, 22);
  room("staterooms", "OFFICERS' STATEROOMS", 11, 190, 214, 6, 28);
  for (const [sx, sz] of [[195, 8], [195, 24], [208, 8], [208, 24]]) {
    g.fill(sx, sx + 1, 11, 11, sz, sz + 1, V.BUNK); g.fill(sx + 3, sx + 3, 11, 11, sz, sz, V.TABLE);
    hangout(sx + 2, 11, sz + 2);
  }
  // Sick bay annex / battle dressing aft, brig, laundry
  wall2(76, 88, 18, 27); door(g, 88, 11, 22);
  room("dressing", "BATTLE DRESSING STATION", 11, 76, 88, 18, 27);
  g.fill(79, 82, 11, 11, 24, 25, V.WHITE); g.fill(84, 85, 11, 11, 20, 20, V.LOCKER);
  g.fill(80, 81, 12, 12, 27, 27, V.RED);       // red cross inlaid on the aft bulkhead
  g.set(77, 11, 20, V.WHITE);                  // plasma bottle rack
  wall2(60, 72, 6, 13); door(g, 72, 11, 9);
  room("brig", "BRIG", 11, 60, 72, 6, 13);
  g.fill(62, 62, 11, 12, 7, 12, V.LOCKER); g.fill(65, 66, 11, 11, 7, 8, V.BUNK);
  wall2(60, 72, 20, 27); door(g, 72, 11, 24);
  room("laundry", "SHIP'S LAUNDRY", 11, 60, 72, 20, 27);
  g.fill(62, 70, 11, 11, 25, 26, V.GALLEY);
  // Steering gear aft
  wall2(30, 44, 11, 23); door(g, 44, 11, 17);
  room("steering", "STEERING GEAR ROOM", 11, 30, 44, 11, 23);
  g.fill(33, 40, 11, 12, 13, 15, V.MACH); g.fill(33, 40, 11, 12, 19, 21, V.MACH);
  g.fill(41, 41, 11, 11, 16, 18, V.BRASS);     // trick wheel — hand steering aft
  g.fill(31, 31, 11, 12, 16, 18, V.GAUGE);     // rudder angle indicators
  hangout(36, 11, 17);

  // ---- third deck (walk 8): Broadway, plot, magazines --------------------
  const wall3 = (x0, x1, z0, z1) => boxWalls(g, x0, x1, 8, 9, z0, z1, V.WALL);
  // Main battery plot. The rangekeeper is a waist-high table facing the
  // forward bulkhead, which carries the live time-range plotting board
  // (main.js hangs the canvas mesh there) — nothing tall enough to bury the
  // operator's sightline in blank steel.
  wall3(128, 148, 5, 13); door(g, 148, 8, 9); door(g, 138, 8, 13);
  room("plot", "MAIN BATTERY PLOT", 8, 128, 148, 5, 13);
  g.fill(132, 137, 8, 8, 7, 8, V.CONSOLE);   // Mk 8 rangekeeper table
  g.set(133, 8, 7, V.GAUGE); g.set(136, 8, 7, V.GAUGE); // dial clusters on the top
  g.set(139, 8, 7, V.CONSOLE); g.set(139, 9, 7, V.RED); // Mk 41 stable vertical + firing keys
  g.fill(141, 145, 8, 9, 6, 6, V.CONSOLE);   // fire control switchboard
  g.fill(141, 145, 9, 9, 6, 6, V.GAUGE);     // its meter row
  g.fill(130, 130, 8, 9, 11, 11, V.RED);     // battle phone / DC riser
  g.fill(129, 129, 8, 9, 8, 10, V.WHITE);    // status boards on the after bulkhead
  g.fill(131, 137, 9, 9, 13, 13, V.CABLE);   // cable tray inlaid along the stbd wall
  g.fill(139, 146, 9, 9, 13, 13, V.CABLE);   // (split around the door at x138)
  g.set(134, 8, 9, V.TABLE); g.set(136, 8, 9, V.TABLE); // operators' stools
  station("plotdesk", "PLOT — MK 8 RANGEKEEPER", 134.5, 8, 10.5, -Math.PI / 2, { kind: "plot" });
  hangout(143, 8, 9); hangout(133, 8, 11);
  // Gyro room next door (deco)
  wall3(116, 126, 5, 12); door(g, 126, 8, 8);
  room("gyro", "GYRO ROOM", 8, 116, 126, 5, 12);
  g.fill(119, 122, 8, 9, 7, 9, V.MACH);
  // 16" powder magazine, serving turret II's barbette. A powder flat is not
  // an empty steel box with a few pipes in it — it is WALLS of powder: brass
  // tank ends racked deck-to-overhead in steel stanchion bays down both
  // sides, one narrow handling aisle to the hoist scuttle, and the sprinkler
  // plumbing overhead that the M key floods through. Six hundred pounds a
  // salvo comes out of this room; it should look like it.
  wall3(180, 194, 5, 13); door(g, 180, 8, 9);
  room("magazine", "16-IN POWDER MAGAZINE", 8, 180, 194, 5, 13);
  for (let px = 182; px <= 191; px++) {
    const rack = (px - 182) % 3 === 2 ? V.LOCKER : V.BRASS; // stanchion bays of stacked tank ends
    g.fill(px, px, 8, 9, 6, 7, rack);
    g.fill(px, px, 8, 9, 11, 12, rack);
  }
  for (let px = 184; px <= 190; px += 3) {    // sprinkler risers plumbed down the stanchions —
    g.set(px, 9, 7, V.RED);                   // the M key's water arrives through these
    g.set(px, 9, 11, V.RED);
  }
  g.fill(192, 192, 8, 9, 9, 9, V.BRASS);      // powder hoist trunk to the barbette
  g.fill(193, 193, 8, 9, 8, 8, V.RED);        // flash scuttle doors flanking the hoist
  g.fill(193, 193, 8, 9, 10, 10, V.RED);
  g.set(193, 9, 9, V.GAUGE);                  // hoist order indicator over the trunk
  g.fill(181, 181, 9, 9, 6, 8, V.RED);        // sprinkler control — the M key's plumbing
  g.fill(181, 181, 8, 8, 6, 6, V.GAUGE);      // magazine temperature board (checked hourly, logged always)
  g.fill(181, 181, 9, 9, 10, 12, V.CABLE);
  hangout(186, 8, 9);
  // Supply storerooms
  wall3(90, 104, 20, 27); door(g, 104, 8, 23);
  room("stores", "SUPPLY STOREROOM", 8, 90, 104, 20, 27);
  g.fill(92, 102, 8, 9, 25, 26, V.LOCKER); g.fill(92, 96, 8, 8, 21, 22, V.LOCKER);

  // Transverse watertight bulkheads with centerline doors, so the lower
  // decks read as frames and passageways instead of open halls.
  // Frames whose centerline is blocked by a barbette get side doors instead.
  const bulkhead = (bx, walk, doors = [16, 17]) => {
    const hb = halfBeam(bx) - 1;
    g.fill(bx, bx, walk, walk + 1, Math.ceil(CZ - hb + 0.5), Math.floor(CZ + hb - 0.5), V.HULL);
    for (const dz of doors) g.fill(bx, bx, walk, walk + 1, dz, dz, V.AIR);
  };
  const SIDE = [8, 9, 25, 26];
  for (const bx of [56, 90, 144, 216]) bulkhead(bx, 11);
  bulkhead(74, 11, SIDE); bulkhead(174, 11, SIDE);
  for (const bx of [56, 112, 152]) bulkhead(bx, 8);
  bulkhead(74, 8, SIDE); bulkhead(200, 8, SIDE);

  // ---- barbettes: armored tubes; turret II's holds the projectile flat ---
  cylinder(g, 196.5, 17.5, 5.5, 8, 15, V.ARMOR);        // turret I
  cylinder(g, 78.5, 17.5, 5.5, 8, 15, V.ARMOR);         // turret III
  cylinder(g, 196.5, 17.5, 7, 14, 15, V.ARMOR);         // collision pads under the
  cylinder(g, 78.5, 17.5, 7, 14, 15, V.ARMOR);          // gunhouse overhangs
  cylinder(g, 172.5, 17.5, 5.5, 3, 18, V.ARMOR);        // turret II riser to 01 level
  cylinder(g, 172.5, 17.5, 7, 17, 18, V.ARMOR);
  cylinderCarve(g, 172.5, 17.5, 4.2, 8, 9);             // projectile flat
  for (let a = 0; a < 8; a++) {                         // ring of 16" shells
    const sx = Math.round(172 + Math.cos(a * Math.PI / 4) * 3), sz = Math.round(17.5 + Math.sin(a * Math.PI / 4) * 3);
    g.fill(sx, sx, 8, 9, sz, sz, V.BARREL);
  }
  room("projflat", "TURRET II PROJECTILE FLAT", 8, 168, 177, 14, 21);
  g.fill(176, 179, 8, 9, 17, 17, V.AIR);                // door through the armor
  g.fill(176, 179, 8, 9, 18, 18, V.AIR);

  // ---- main deck deckhouse (walk 14) -------------------------------------
  // Outer shell
  boxWalls(g, 92, 166, 14, 15, 7, 27, V.SUPER);
  g.fill(92, 166, 16, 16, 7, 27, V.DECKSTEEL); // 01-level plating overhead
  // interior floor
  for (let x = 93; x <= 165; x++) for (let z = 8; z <= 26; z++) g.set(x, 13, z, V.FLOOR);
  // centerline passage walls
  g.fill(94, 164, 14, 15, 15, 15, V.WALL);
  g.fill(94, 164, 14, 15, 19, 19, V.WALL);
  g.carve(94, 164, 14, 15, 16, 18); // the passage itself
  // weather doors fore/aft/side
  door(g, 92, 14, 17); door(g, 166, 14, 17); door(g, 128, 14, 7); door(g, 128, 14, 27);
  g.fill(128, 128, 14, 15, 8, 14, V.AIR); g.fill(128, 128, 14, 15, 20, 26, V.AIR); // athwartships passage
  g.fill(127, 127, 14, 15, 8, 14, V.WALL); g.fill(129, 129, 14, 15, 8, 14, V.WALL);
  g.fill(127, 127, 14, 15, 20, 26, V.WALL); g.fill(129, 129, 14, 15, 20, 26, V.WALL);
  g.fill(128, 128, 14, 15, 15, 19, V.AIR);  // join both legs to the fore-aft passage
  g.fill(129, 129, 14, 15, 22, 22, V.AIR);  // passage door into sick bay's east bay
  // Wardroom (officers' mess) forward port
  partition(g, 14, 130, 164, 8, 14, [[147, 11]]);
  room("wardroom", "WARDROOM", 14, 130, 164, 8, 14);
  g.fill(136, 144, 14, 14, 10, 11, V.TABLE); g.fill(152, 158, 14, 14, 10, 11, V.TABLE);
  for (const cx of [137, 140, 143, 153, 156]) { g.set(cx, 14, 9, V.BUNK); g.set(cx, 14, 12, V.BUNK); } // chairs
  g.fill(131, 131, 14, 14, 9, 13, V.GALLEY);   // sideboard and coffee service
  g.set(131, 15, 11, V.BRASS);                  // the wardroom coffee urn, polished daily
  g.fill(147, 149, 15, 15, 8, 8, V.WHITE);      // ship's plaque and photos on the fwd wall
  door(g, 138, 14, 15); door(g, 138, 14, 14); hangout(140, 14, 12); hangout(155, 14, 12);
  // Captain's cabin forward starboard
  partition(g, 14, 146, 164, 20, 26, []);
  room("cabin", "CAPTAIN'S CABIN", 14, 146, 164, 20, 26);
  g.fill(158, 160, 14, 14, 22, 24, V.TABLE); g.fill(149, 150, 14, 14, 24, 25, V.BUNK);
  door(g, 152, 14, 19); door(g, 152, 14, 20); hangout(155, 14, 22);
  // Sick bay starboard amidships
  partition(g, 14, 112, 144, 20, 26, [[126, 20]]);
  g.fill(128, 128, 14, 15, 20, 20, V.AIR); // the athwartships passage runs through
  g.fill(128, 128, 14, 15, 26, 26, V.AIR);
  room("sickbay", "SICK BAY", 14, 112, 144, 20, 26);
  for (const wx of [116, 122, 134, 140]) g.fill(wx, wx + 1, 14, 14, 23, 24, V.WHITE);
  g.fill(113, 113, 14, 15, 21, 21, V.RED);
  g.fill(115, 115, 14, 15, 25, 25, V.WHITE); g.fill(118, 119, 15, 15, 26, 26, V.RED); // medicine cabinet + red cross inlay
  g.fill(137, 138, 14, 14, 25, 25, V.GAUGE);  // sterilizer and instrument tray
  door(g, 120, 14, 19); door(g, 120, 14, 20); hangout(130, 14, 22);
  // Galley port amidships
  partition(g, 14, 112, 126, 8, 14, []);
  room("galley", "GALLEY", 14, 112, 126, 8, 14);
  g.fill(114, 124, 14, 14, 9, 10, V.GALLEY); g.fill(114, 118, 14, 14, 12, 12, V.GALLEY);
  g.fill(115, 117, 15, 15, 8, 8, V.CABLE);   // vent hood over the ranges
  g.set(114, 14, 9, V.RED); g.set(121, 14, 9, V.RED); // kettle burners
  g.fill(123, 124, 14, 15, 12, 12, V.WHITE); // reefer door
  door(g, 120, 14, 15); door(g, 120, 14, 14); hangout(121, 14, 13);
  // Radio central
  partition(g, 14, 94, 110, 20, 26, []);
  room("radio", "RADIO CENTRAL", 14, 94, 110, 20, 26);
  g.fill(96, 106, 14, 14, 24, 25, V.CONSOLE); g.fill(96, 100, 15, 15, 25, 25, V.SCREEN);
  g.fill(102, 106, 15, 15, 25, 25, V.GAUGE);  // transmitter dials above the second bay
  g.fill(108, 108, 14, 15, 24, 24, V.CABLE);  // antenna trunk up through the overhead
  g.fill(96, 97, 14, 14, 21, 21, V.TABLE);    // message desk
  door(g, 102, 14, 19); door(g, 102, 14, 20); hangout(99, 14, 22);
  // The gedunk (soda fountain) — morale is a weapon system
  partition(g, 14, 94, 110, 8, 14, []);
  room("gedunk", "GEDUNK — SODA FOUNTAIN", 14, 94, 110, 8, 14);
  g.fill(96, 106, 14, 14, 11, 11, V.GALLEY); g.fill(97, 105, 14, 14, 9, 9, V.TABLE);
  for (const sx of [98, 101, 104]) g.set(sx, 14, 12, V.RED); // red vinyl stools
  g.set(96, 14, 9, V.BRASS);                  // the soda taps
  g.fill(99, 103, 15, 15, 8, 8, V.WHITE);     // menu board: "GEEDUNK — ICE CREAM 5¢"
  door(g, 100, 14, 15); door(g, 100, 14, 14); hangout(102, 14, 13); hangout(98, 14, 13);

  // ---- superstructure upper works ----------------------------------------
  // 02 level block on the deckhouse roof: CIC forward, uptakes aft.
  boxSolid(g, 118, 160, 17, 19, 9, 25, V.SUPER);
  g.fill(118, 160, 19, 19, 9, 25, V.DECKSTEEL);
  carveBox(g, 140, 158, 17, 18, 10, 24); // CIC
  for (let x = 140; x <= 158; x++) for (let z = 10; z <= 24; z++) g.set(x, 16, z, V.FLOOR);
  room("cic", "COMBAT INFORMATION CENTER", 17, 140, 158, 10, 24);
  g.fill(144, 149, 17, 17, 21, 22, V.CONSOLE);  // DRT table
  g.fill(145, 148, 17, 17, 21, 21, V.GAUGE);    // its glass trace top
  g.fill(151, 154, 17, 18, 11, 11, V.CONSOLE);  // radar consoles
  g.fill(144, 146, 17, 18, 11, 11, V.CONSOLE);
  g.fill(144, 145, 18, 18, 11, 11, V.GAUGE);    // A-scope faces
  g.fill(156, 156, 17, 18, 15, 19, V.WHITE);    // vertical plot: grease-pencil status board
  g.fill(141, 141, 17, 18, 13, 13, V.RED);      // battle phones by the door
  g.fill(139, 139, 18, 18, 14, 20, V.CABLE);    // cable trays inlaid in the fwd bulkhead
  // Station names lead with the compartment the orders call them by — an
  // order says "designate at CIC", so the T menu must say CIC somewhere.
  station("radar", "CIC — SG RADAR SCOPE", 152.5, 17, 13.5, -Math.PI / 2, { kind: "radar" });
  hangout(146, 17, 20); hangout(148, 17, 14);
  // port-side corridor out to the 01 level, clear of the uptakes
  g.fill(118, 139, 17, 18, 10, 11, V.AIR);
  for (let x = 118; x <= 139; x++) for (let z = 10; z <= 11; z++) g.set(x, 16, z, V.FLOOR);

  // 03 level tower block: chart house + captain's sea cabin
  boxSolid(g, 138, 158, 20, 22, 10, 24, V.SUPER);
  g.fill(138, 158, 22, 22, 10, 24, V.DECKSTEEL);
  carveBox(g, 140, 156, 20, 21, 11, 23);
  for (let x = 140; x <= 156; x++) for (let z = 11; z <= 23; z++) g.set(x, 19, z, V.FLOOR);
  room("charthouse", "CHART HOUSE", 20, 140, 156, 11, 23);
  g.fill(144, 149, 20, 20, 13, 14, V.TABLE); g.fill(152, 154, 20, 20, 19, 21, V.BUNK);
  g.set(145, 20, 13, V.GAUGE);                 // dead-reckoning tracer on the chart flat
  g.fill(141, 141, 20, 21, 19, 21, V.LOCKER);  // chart lockers
  g.fill(150, 152, 21, 21, 11, 11, V.CABLE);   // wall conduit to the bridge above
  g.fill(138, 139, 20, 21, 17, 17, V.AIR); // door (2-thick wall)
  hangout(146, 20, 18);

  // Navigation bridge (walk 23): pilot house with open window band
  boxSolid(g, 140, 158, 23, 25, 11, 23, V.SUPER);
  g.fill(140, 158, 25, 25, 11, 23, V.DECKSTEEL);
  carveBox(g, 142, 156, 23, 24, 12, 22);
  for (let x = 142; x <= 156; x++) for (let z = 12; z <= 22; z++) g.set(x, 22, z, V.FLOOR);
  room("bridge", "NAVIGATION BRIDGE", 23, 142, 156, 12, 22);
  // window band: open air with mullion posts, wrapping the forward half
  for (let z = 12; z <= 22; z++) if ((z - 12) % 3 !== 0) { g.set(157, 24, z, V.AIR); g.set(158, 24, z, V.AIR); }
  for (let x = 148; x <= 156; x++) if ((x - 148) % 3 !== 0) { g.set(x, 24, 11, V.AIR); g.set(x, 24, 23, V.AIR); }
  g.fill(153, 153, 23, 23, 16, 18, V.CONSOLE); // helm + engine order telegraph
  g.fill(154, 154, 23, 23, 17, 17, V.CONSOLE);
  g.set(153, 23, 17, V.BRASS);                 // the wheel itself
  g.set(155, 23, 19, V.BRASS);                 // binnacle at the centerline window
  g.set(155, 23, 15, V.RED);                   // engine order telegraph, brass and red
  g.fill(148, 149, 23, 23, 13, 14, V.TABLE);   // chart flat
  g.set(148, 23, 20, V.GAUGE);                 // radar repeater
  g.fill(143, 143, 23, 24, 12, 12, V.RED);     // battle phones aft corner
  station("helm", "NAV BRIDGE — HELM & TELEGRAPH", 151.5, 23, 17.5, 0, { kind: "helm" });
  g.fill(140, 141, 23, 24, 17, 17, V.AIR);     // door in from the 03 roof
  hangout(144, 23, 14); hangout(144, 23, 20);

  // Fire-control tower above the pilot house, with internal ladder shaft
  boxSolid(g, 144, 152, 26, 29, 13, 21, V.SUPER);
  g.fill(146, 150, 26, 29, 15, 19, V.SUPER);
  carveBox(g, 147, 149, 23, 30, 16, 18); // shaft from bridge deck up
  // director platform
  g.fill(143, 153, 30, 30, 12, 22, V.DECKSTEEL);
  boxWalls(g, 143, 153, 31, 31, 12, 22, V.SUPER); // parapet
  g.fill(148, 148, 30, 30, 16, 18, V.AIR); // hatch: the shaft opens through the platform deck
  room("director", "MK 38 DIRECTOR STATION", 31, 143, 153, 12, 22);
  station("director", "MK 38 DIRECTOR & RANGEFINDER", 148.5, 31, 17.5, 0, { kind: "director" });
  ladderCol(g, ladders, 148, 23, 31, 17);

  // Turret officer's booth aft of turret II barbette (01 level, on a riser
  // block that carries it down to the main deck)
  boxSolid(g, 177, 183, 14, 19, 14, 20, V.SUPER);
  carveBox(g, 178, 182, 17, 18, 15, 19);
  for (let x = 178; x <= 182; x++) for (let z = 15; z <= 19; z++) g.set(x, 16, z, V.FLOOR);
  room("turretbooth", "TURRET II LOCAL CONTROL", 17, 178, 182, 15, 19);
  g.fill(178, 178, 17, 18, 16, 16, V.CONSOLE);
  station("turretlocal", "TURRET II PERISCOPE", 180.5, 17, 17.5, Math.PI, { kind: "turret" });

  // Funnels
  boxSolid(g, 124, 132, 17, 33, 13, 21, V.SUPER);
  g.fill(124, 132, 34, 35, 13, 21, V.FUNNEL);
  g.fill(125, 131, 34, 36, 14, 20, V.FUNNEL);
  boxSolid(g, 104, 112, 17, 31, 13, 21, V.SUPER);
  g.fill(104, 112, 32, 33, 13, 21, V.FUNNEL);
  g.fill(105, 111, 32, 34, 14, 20, V.FUNNEL);

  // Aft fire-control tower + aft director
  boxSolid(g, 92, 100, 17, 27, 13, 21, V.SUPER);
  g.fill(92, 100, 28, 28, 12, 22, V.DECKSTEEL);

  // Foremast with yardarm, stepped abaft the tower (SK bedspring rides it)
  g.fill(134, 135, 20, 44, 16, 17, V.SUPER);
  g.fill(134, 134, 40, 40, 8, 25, V.SUPER); // yard
  // Mainmast aft
  g.fill(95, 96, 29, 38, 16, 17, V.SUPER);

  // ---- secondary battery: five twin 5"/38 mounts per side ---------------
  const fives = [];
  for (const [mx, mz, side] of [
    [104, 3, -1], [118, 3, -1], [136, 3, -1], [150, 3, -1], [162, 5, -1],
    [104, 31, 1], [118, 31, 1], [136, 31, 1], [150, 31, 1], [162, 29, 1],
  ]) {
    const z0 = side < 0 ? mz : mz - 3;
    boxSolid(g, mx, mx + 4, 14, 16, z0, z0 + 3, V.TURRET);
    g.fill(mx + 1, mx + 3, 17, 17, z0 + 1, z0 + 2, V.TURRET);
    // stubby twin barrels poking outboard
    const bz = side < 0 ? z0 - 1 : z0 + 4;
    g.fill(mx + 1, mx + 1, 15, 15, Math.min(bz, z0), Math.max(bz, z0 + 3), V.BARREL);
    g.fill(mx + 3, mx + 3, 15, 15, Math.min(bz, z0), Math.max(bz, z0 + 3), V.BARREL);
    fives.push(new THREE.Vector3(mx + 2, 16, side < 0 ? z0 : z0 + 3.5));
  }

  // 40 mm quad tubs: bow pair, stern pair, waist
  const forties = [];
  for (const [tx, tz] of [[232, 10], [232, 24], [16, 13], [16, 21], [86, 6], [86, 28], [168, 6], [168, 28]]) {
    cylinder(g, tx, tz + 0.5, 2.8, 14, 14, V.HULL);
    g.fill(tx - 1, tx + 1, 15, 15, tz - 1, tz + 1, V.MACH);
    g.fill(tx - 2, tx + 2, 16, 16, tz, tz, V.BARREL);
    forties.push(new THREE.Vector3(tx, 16, tz + 0.5));
  }

  // ---- weather deck dressing ---------------------------------------------
  // Breakwater ahead of turret I
  for (let i = 0; i <= 10; i++) {
    g.set(224 + i, 14, 17 - i, V.HULL);
    g.set(224 + i, 14, 17 + i, V.HULL);
  }
  // Anchor chains to the hawse
  for (let x = 246; x <= 264; x += 2) { g.set(x, 14, 13, V.PIPE); g.set(x, 14, 21, V.PIPE); }
  g.fill(244, 246, 14, 14, 12, 14, V.MACH); g.fill(244, 246, 14, 14, 20, 22, V.MACH); // windlasses
  // Bollards
  for (let x = 30; x <= 250; x += 34) {
    const hb = halfBeam(x);
    g.set(x, 14, Math.ceil(CZ - hb + 1.5), V.HULL);
    g.set(x, 14, Math.floor(CZ + hb - 1.5), V.HULL);
  }
  // Liferaft canisters along the deckhouse sides
  for (let x = 100; x <= 160; x += 12) { g.fill(x, x + 2, 15, 15, 6, 6, V.WHITE); g.fill(x, x + 2, 15, 15, 28, 28, V.WHITE); }
  // Ship's boats in chocks aft of the stack
  for (const [bx, bz] of [[70, 8], [70, 24]]) {
    g.fill(bx, bx + 9, 14, 14, bz, bz + 2, V.WHITE);
    g.fill(bx + 1, bx + 8, 15, 15, bz + 1, bz + 1, V.TABLE);
  }
  // Fantail: Kingfisher catapult rails + crane
  g.fill(8, 34, 14, 14, 9, 9, V.LOCKER); g.fill(8, 34, 14, 14, 25, 25, V.LOCKER);
  g.fill(12, 13, 14, 20, 16, 17, V.SUPER); g.fill(12, 18, 20, 20, 16, 17, V.SUPER); // crane
  room("fantail", "FANTAIL", 14, 4, 40, 6, 28);
  room("focsle", "FORECASTLE", 14, 220, 268, 8, 26);
  room("qtrdeck", "QUARTERDECK", 14, 42, 90, 4, 30);
  hangout(50, 14, 8); hangout(50, 14, 26); hangout(240, 14, 17); hangout(20, 14, 17);
  hangout(200, 14, 5); hangout(200, 14, 29);

  // ---- ladder trunks ------------------------------------------------------
  ladderCol(g, ladders, 186, 8, 14, 16);   // fwd trunk: 3rd <-> main
  ladderCol(g, ladders, 120, 3, 14, 20);   // amidships: hold <-> main
  ladderCol(g, ladders, 82, 8, 14, 16);    // aft trunk: 3rd <-> main
  ladderCol(g, ladders, 148, 8, 11, 12);   // plot escape trunk: 3rd <-> 2nd
  // external superstructure ladders. The aft pair must land clear of the aft
  // fire-control tower (x92-100 spans z13-21 from the 01 level up — a ladder
  // topping out into it is a wall-climb to nowhere).
  extLadder(g, ladders, 91, 14, 17, 24);   // main deck -> 01 (aft face, stbd)
  extLadder(g, ladders, 91, 14, 17, 10);   // main deck -> 01 (aft face, port)
  extLadder(g, ladders, 167, 14, 17, 16);  // main deck -> 01 (fwd face)
  g.fill(167, 167, 18, 18, 16, 16, V.AIR); // head clearance: the barbette ring runs to y18 above this ladder
  extLadder(g, ladders, 117, 17, 20, 16);  // 01 -> 02 roof (abaft the CIC block)
  extLadder(g, ladders, 161, 17, 20, 16);  // 01 -> 02 roof (fwd face — the bridge route)
  extLadder(g, ladders, 137, 20, 23, 12);  // 02 roof -> 03 roof (bridge door at x140)
  extLadder(g, ladders, 137, 20, 23, 22);  // 02 roof -> 03 roof (stbd twin)
  // trunk from the projectile flat up into the turret officer's booth
  ladderCol(g, ladders, 179, 8, 17, 17);

  // ---- rotating turret meshes --------------------------------------------
  const turretDefs = [
    { id: "T1", cx: 196.5, cz: 17.5, baseY: 14, aft: false },
    { id: "T2", cx: 172.5, cz: 17.5, baseY: 17, aft: false },
    { id: "T3", cx: 78.5, cz: 17.5, baseY: 14, aft: true },
  ];
  const turrets = turretDefs.map((d) => makeTurret(d));

  // Director housing (rotating) on the tower-top platform
  const directorMesh = makeDirectorHousing();
  directorMesh.position.set(148.5, 31, 17.5);

  const aftDirectorMesh = makeDirectorHousing();
  aftDirectorMesh.scale.setScalar(0.8);
  aftDirectorMesh.position.set(96, 29, 17);

  // Radar antennas (rotating)
  const skRadar = makeSKRadar(); skRadar.position.set(135, 45.5, 17);
  const sgRadar = makeSGRadar(); sgRadar.position.set(95.5, 39, 17);

  // Spawn: on the quarterdeck by the aft deckhouse door
  const spawn = new THREE.Vector3(88, 14, 17.5);

  return {
    grid: g, rooms, stations, hangouts, ladders, turrets,
    directorMesh, aftDirectorMesh, skRadar, sgRadar,
    fives, forties,
    funnelTops: [new THREE.Vector3(128, 37, 17), new THREE.Vector3(108, 35, 17)],
    spawn,
    ladderMesh: makeLadders(g, ladders),
  };
}

// ---------------------------------------------------------------------------
// Visible ladders. LADDER cells are non-solid so the greedy mesher never
// emits a face for them — climbable but invisible. Build real geometry:
// two brass rails and rungs per column, one InstancedMesh for the whole ship,
// each column set flat against whichever wall it actually serves.
function makeLadders(g, ladders) {
  const boxes = []; // {x,y,z, sx,sy,sz, shade}
  for (const L of ladders) {
    // true column extent from the grid (extLadder and ladderCol fill differently)
    let y0 = L.y0;
    while (g.climbable(L.x, y0 - 1, L.z)) y0--;
    let y1 = y0;
    while (g.climbable(L.x, y1 + 1, L.z)) y1++;
    const h = y1 - y0 + 1;
    // which side is the wall on?
    let dir = [1, 0], bc = -1;
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      let c = 0;
      for (let y = y0; y <= y1; y++) if (g.solid(L.x + dx, y, L.z + dz)) c++;
      if (c > bc) { bc = c; dir = [dx, dz]; }
    }
    const [dx, dz] = dir;
    const px = -dz, pz = dx; // across the ladder
    const cx = L.x + 0.5 + dx * 0.36, cz = L.z + 0.5 + dz * 0.36;
    const yMid = y0 + h / 2;
    for (const s of [-1, 1]) // rails
      boxes.push({ x: cx + px * 0.30 * s, y: yMid, z: cz + pz * 0.30 * s, sx: 0.08, sy: h, sz: 0.08, shade: 0.8 });
    const rungs = Math.max(2, Math.round(h / 0.34));
    for (let i = 0; i < rungs; i++)
      boxes.push({
        x: cx, y: y0 + (i + 0.5) * (h / rungs), z: cz,
        sx: Math.abs(px) * 0.62 + 0.07, sy: 0.06, sz: Math.abs(pz) * 0.62 + 0.07, shade: 1,
      });
  }
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({ color: 0xffffff }),
    boxes.length);
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), v = new THREE.Vector3(), sc = new THREE.Vector3();
  const gold = new THREE.Color(0xcfa14e), c = new THREE.Color();
  boxes.forEach((b, i) => {
    m.compose(v.set(b.x, b.y, b.z), q, sc.set(b.sx, b.sy, b.sz));
    mesh.setMatrixAt(i, m);
    mesh.setColorAt(i, c.copy(gold).multiplyScalar(b.shade));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.name = "ladders";
  return mesh;
}

// ---------------------------------------------------------------------------
// small builders

function boxSolid(g, x0, x1, y0, y1, z0, z1, v) { g.fill(x0, x1, y0, y1, z0, z1, v); }
function carveBox(g, x0, x1, y0, y1, z0, z1) { g.fill(x0, x1, y0, y1, z0, z1, V.AIR); }
// Walls around a box footprint (leaves the inside alone).
function boxWalls(g, x0, x1, y0, y1, z0, z1, v) {
  g.fill(x0, x1, y0, y1, z0, z0, v);
  g.fill(x0, x1, y0, y1, z1, z1, v);
  g.fill(x0, x0, y0, y1, z0, z1, v);
  g.fill(x1, x1, y0, y1, z0, z1, v);
}
// A 1-wide, 2-high doorway at (x, walkY, z).
function door(g, x, y, z) { g.fill(x, x, y, y + 1, z, z, V.AIR); }
// Interior partition: outline walls with doors punched later by door().
function partition(g, walkY, x0, x1, z0, z1, extraDoors) {
  boxWalls(g, x0, x1, walkY, walkY + 1, z0, z1, V.WALL);
  for (const [dx, dz] of extraDoors) door(g, dx, walkY, dz);
}
// Vertical ladder column from walk level y0 to walk level y1 at (x, z):
// climbable cells all the way, hole punched through each deck slab.
function ladderCol(g, ladders, x, y0, y1, z) {
  for (let y = y0; y <= y1 + 1; y++) g.set(x, y, z, V.LADDER);
  ladders.push({ x, z, y0, y1 });
}
function extLadder(g, ladders, x, y0, y1, z) {
  for (let y = y0; y <= y1; y++) g.set(x, y, z, V.LADDER);
  ladders.push({ x, z, y0, y1 });
}
// Solid vertical cylinder (barbettes, gun tubs).
function cylinder(g, cx, cz, r, y0, y1, v) {
  for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++)
    for (let z = Math.floor(cz - r); z <= Math.ceil(cz + r); z++)
      if ((x + 0.5 - cx) ** 2 + (z + 0.5 - cz) ** 2 <= r * r) g.fill(x, x, y0, y1, z, z, v);
}
function cylinderCarve(g, cx, cz, r, y0, y1) {
  for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++)
    for (let z = Math.floor(cz - r); z <= Math.ceil(cz + r); z++)
      if ((x + 0.5 - cx) ** 2 + (z + 0.5 - cz) ** 2 <= r * r) g.fill(x, x, y0, y1, z, z, V.AIR);
}

// ---------------------------------------------------------------------------
// Rotating turret: voxel gunhouse shell + three elevating 16" rifles.
// Local frame: +X is the firing direction, pivot at the barbette axis.
function makeTurret(def) {
  const tg = new VoxelGrid(18, 7, 13);
  // rear body
  tg.fill(0, 12, 0, 3, 1, 11, V.TURRET);
  tg.fill(1, 9, 4, 4, 2, 10, V.TURRET);          // stepped crown
  tg.fill(13, 14, 0, 2, 2, 10, V.TURRET);        // sloped face (stepped)
  tg.fill(0, 0, 1, 2, 0, 0, V.TURRET);           // rangefinder ears
  tg.fill(0, 0, 1, 2, 12, 12, V.TURRET);
  tg.fill(1, 3, 5, 5, 5, 7, V.LOCKER);           // periscope hoods
  const shell = gridToMeshes(tg);
  shell.position.set(-7, 0, -6.5); // pivot: 7 m in from the rear plate

  const group = new THREE.Group();
  group.add(shell);
  group.position.set(def.cx, def.baseY, def.cz);
  if (def.aft) group.rotation.y = Math.PI;

  const barrels = [];
  for (const bz of [-4, 0, 4]) {
    const bg = new VoxelGrid(18, 2, 1);
    bg.fill(0, 9, 0, 0, 0, 0, V.BARREL);
    const inner = gridToMeshes(bg);
    inner.scale.set(1.7, 0.9, 0.9);
    inner.position.set(0, -0.45, -0.45);
    const pivot = new THREE.Group();
    pivot.add(inner);
    pivot.position.set(5.5, 2.0, bz);
    group.add(pivot);
    barrels.push(pivot);
  }
  return {
    ...def,
    group, barrels,
    train: def.aft ? Math.PI : 0,   // current train angle, ship-relative
    orderTrain: null, orderElev: 0.05, elev: 0.05,
    ready: false, disabled: false,
    muzzleLen: 22.5,
  };
}

function makeDirectorHousing() {
  const dg = new VoxelGrid(7, 4, 21);
  dg.fill(1, 5, 0, 2, 7, 13, V.TURRET);          // housing
  dg.fill(2, 4, 3, 3, 8, 12, V.TURRET);          // radar shelf
  dg.fill(2, 4, 0, 1, 8, 12, V.AIR);             // open interior for the crew
  dg.fill(5, 5, 0, 1, 9, 11, V.AIR);             // vision slit
  dg.fill(2, 3, 1, 1, 1, 19, V.BARREL);          // rangefinder tube, full width
  dg.fill(2, 3, 0, 2, 0, 0, V.TURRET);           // rangefinder end hoods
  dg.fill(2, 3, 0, 2, 20, 20, V.TURRET);
  const mesh = gridToMeshes(dg);
  mesh.position.set(-3.5, 0, -10.5);
  const group = new THREE.Group();
  group.add(mesh);
  return group;
}

function makeSKRadar() {
  const rg = new VoxelGrid(1, 5, 6);
  rg.fill(0, 0, 0, 4, 0, 5, V.LOCKER);
  rg.fill(0, 0, 1, 3, 1, 4, V.MACH);
  const mesh = gridToMeshes(rg);
  mesh.scale.set(0.5, 1, 1);
  mesh.position.set(-0.25, 0, -3);
  const group = new THREE.Group();
  group.add(mesh);
  return group;
}
function makeSGRadar() {
  const rg = new VoxelGrid(1, 2, 3);
  rg.fill(0, 0, 0, 1, 0, 2, V.LOCKER);
  const mesh = gridToMeshes(rg);
  mesh.position.set(-0.5, 0, -1.5);
  const group = new THREE.Group();
  group.add(mesh);
  return group;
}
